import type { LandingHeroImagePublic, PublicProductSummary } from '@repo/api-contracts';
import { ArrowDown } from 'lucide-react';
import type { ReactElement } from 'react';
import { buildHeroSlides } from './build-hero-slides';
import { HeroSlider } from './hero-slider';

interface HomeHeroProps {
  /** admin 배너(landing/hero) — 배너 한 장 = 슬라이드 한 장, 문구는 배너에 연결한 상품에서 온다. */
  readonly banners: readonly LandingHeroImagePublic[];
  /** 배너 목록을 아직 받는 중 — 그동안 추천 목록으로 먼저 그렸다가 배너로 바뀌어 깜빡이지 않게 기다린다. */
  readonly isBannersPending: boolean;
  /** 추천 목록 — 배너가 없을 때만 앞에서부터 사진 없는 슬라이드로 싣는다. */
  readonly products: readonly PublicProductSummary[];
  /** 추천 목록 상태 — 배너가 없을 때만 불러오는 중·오류·빈 목록 안내에 쓴다. */
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
  /** 오른쪽 아래 아래로 버튼이 이동할 다음 섹션의 id. */
  readonly nextSectionId: string;
}

/**
 * HOME 첫 화면 — 화면 폭을 채우는 배너 슬라이더(사진·문구가 함께 가로로 넘어간다),
 * 왼쪽 아래 이전·번호·다음, 오른쪽 아래 다음 섹션으로 내려가는 버튼.
 * 높이는 처음부터 화면 한 장이다 — 큰 헤더가 스크롤로 위로 밀려 나가면 사진이 화면을 꽉 채운다.
 */
export const HomeHero = ({
  banners,
  isBannersPending,
  products,
  isPending,
  isError,
  onRetry,
  nextSectionId,
}: HomeHeroProps): ReactElement => {
  const slides = buildHeroSlides(banners, products);
  // 배너가 있으면 추천 목록 상태와 상관없이 배너를 보여 준다. 안내 문구는 배너가 없을 때만 낸다.
  const message =
    banners.length > 0
      ? null
      : isBannersPending || isPending
        ? '추천 상품을 불러오는 중입니다.'
        : isError
          ? '추천 상품을 불러오지 못했습니다.'
          : slides.length === 0
            ? '공개된 상품이 아직 없습니다.'
            : null;

  return (
    <section
      aria-labelledby="home-hero-heading"
      // 높이는 첫 화면에 딱 맞춘다 — 공지 띠(2.25rem) 아래로 화면 끝까지, 아래로 넘쳐 잘리지 않게.
      className="relative isolate h-[calc(100dvh-2.25rem)] min-h-[32rem] overflow-hidden bg-primary"
    >
      {/* 화면에 보이는 제목은 없지만 페이지의 h1 은 둔다(스크린리더·문서 구조). */}
      <h1 id="home-hero-heading" className="sr-only">
        SuperStructure — 기업·단체 커스텀 굿즈 제작
      </h1>

      {message !== null ? (
        // 안내는 배너가 없을 때만 나오므로 늘 사진 없는 흑연 판 위의 흰 글자다.
        <div className="absolute inset-0 flex items-center px-5 pt-28 sm:px-8 sm:pt-36 xl:px-14">
          <p role={isError ? 'alert' : 'status'} className="text-lg text-primary-foreground">
            {message}{' '}
            {isError ? (
              <button
                type="button"
                onClick={onRetry}
                className="font-semibold underline underline-offset-4"
              >
                다시 시도
              </button>
            ) : null}
          </p>
        </div>
      ) : (
        <HeroSlider slides={slides} />
      )}

      <a
        href={`#${nextSectionId}`}
        aria-label="다음 섹션으로 이동"
        className="absolute right-5 bottom-8 z-30 flex size-12 items-center justify-center bg-primary text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:right-8 sm:bottom-12 sm:size-14 xl:right-14"
      >
        <ArrowDown aria-hidden strokeWidth={1.5} className="size-5" />
      </a>
    </section>
  );
};
